import {Button, closeDialog} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {useSourcingDeleteUserCarvagoBuyMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {openCarvagoTransactionWorkspace} from '../../utils/openCarvagoTransactionWorkspace';

interface SourcingCarvagoRemoveDialogProps extends TestIdProps {
  externalId: string;
  dialogId: string;
  onRemoved: () => void;
}

export function SourcingCarvagoRemoveDialog(props: SourcingCarvagoRemoveDialogProps) {
  // The managed host survives the optimistic removal of the list row that opened it.
  const [removeFromCarvago, {isLoading}] = useSourcingDeleteUserCarvagoBuyMutation({
    fixedCacheKey: props.dialogId,
  });
  const handleConfirm = () => {
    if (isLoading) {
      return;
    }
    removeFromCarvago({externalId: props.externalId})
      .unwrap()
      .then(() => {
        props.onRemoved();
        closeDialog(props.dialogId);
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <Box
        backgroundColor="palettes.neutral.20.100"
        border="1px solid"
        borderColor="palettes.neutral.40.100"
        borderRadius="small"
        padding={4}
      >
        <VStack spacing={4}>
          <Text size="small">{i18n.t('page.sourcing.labels.carvagoBuyRemoveDescription')}</Text>
          <Button
            title={i18n.t('entity.sourcing.actions.manageRequests')}
            data-testid={suffixTestId('manageRequests', props)}
            rightIcon="action/open_in_new"
            onClick={openCarvagoTransactionWorkspace}
            variant="secondary"
          />
        </VStack>
      </Box>
      <HStack spacing={2} justify="flex-end">
        <Button
          title={i18n.t('general.actions.cancel')}
          data-testid={suffixTestId('cancel', props)}
          onClick={() => closeDialog(props.dialogId)}
          variant="secondary"
          isDisabled={isLoading}
        />
        <Button
          title={i18n.t('general.actions.removeFromList')}
          data-testid={suffixTestId('confirm', props)}
          onClick={handleConfirm}
          variant="primary"
          isLoading={isLoading}
          isDisabled={isLoading}
        />
      </HStack>
    </VStack>
  );
}
